<template>
  <template v-if="!loading && metadataLoaded">
    <refonte-card>
      <refonte-filters-tabs>
        <template v-slot:default>
          <inscriptions-buttons-filters />
        </template>
      </refonte-filters-tabs>
      <inscriptions-filters
        v-bind:metadatas="metadatas"
        v-if="metadataLoaded" />
    </refonte-card>
    <refonte-card class="mt-[8px] !gap-0 !p-0">
      <inscriptions-counts v-bind:inscription_count="inscriptionCount" />
      <div class="mt-2 max-h-[70vh] overflow-y-auto">
        <inscriptions-content
          v-if="!dataLoading"
          v-bind:fetch="fetchData" />
        <template v-else>
          <spinner
            sizes="w-20 h-20"
            thickness="border-8" />
        </template>
        <refonte-load-more
          v-bind:show_load="page + 1 != totalPages"
          v-bind:load="load"
          v-bind:total_of_elements="totalElement"
          v-bind:loading="showLoadMore"
          v-bind:number_of_elements="inscriptions.length" />
      </div>
    </refonte-card>
  </template>
  <template v-else>
    <spinner
      sizes="w-20 h-20"
      thickness="border-8" />
  </template>
</template>

<script setup lang="ts">
  import RefonteCard from '@/components/refonte/cards/refonte-card.vue';
  import RefonteLoadMore from '@/components/refonte/inputs/refonte-load-more.vue';
  import { onMounted, ref, watch } from 'vue';
  import Spinner from '@/components/utils/spinner.vue';
  import InscriptionsContent from '@/views/_espace/_refonte/gestion/_inscriptions/inscriptions-content.vue';
  import InscriptionsCounts from '@/views/_espace/_refonte/gestion/_inscriptions/inscriptions-counts.vue';
  import {
    INSCRIPTIONS_FILTERS_CONFIGURATION,
    useInscriptionFilterStore,
    useInscriptionStore
  } from '@/stores/inscriptions';
  import { storeToRefs } from 'pinia';
  import { useEntrepriseStore } from '@/stores/entreprise';
  import InscriptionsFilters from '@/views/_espace/_refonte/gestion/_inscriptions/inscriptions-filters.vue';
  import { useRoute, useRouter } from 'vue-router';
  import api_utils from '@/mixin/api_utils';
  import RefonteFiltersTabs from '@/components/refonte/filters/refonte-filters-tabs.vue';
  import filters_utils from '@/mixin/utils/filters_utils';
  import {
    Filters,
    InscriptionCount,
    InscriptionMetadata
  } from '@/types/gestion/inscriptions-filters-types';
  import { API } from '@/http-common';
  import InscriptionsButtonsFilters from '@/views/_espace/_refonte/gestion/_inscriptions/inscriptions-buttons-filters.vue';

  const loading = ref(false);

  const entrepriseStore = useEntrepriseStore();
  const { activeClientIds } = storeToRefs(entrepriseStore);
  const inscriptionFilterStore = useInscriptionFilterStore();

  const { filters } = storeToRefs(inscriptionFilterStore);

  const dataLoading = ref<boolean>(false);
  const inscriptionStore = useInscriptionStore();

  const { inscriptions, page, totalPages, totalElement } =
    storeToRefs(inscriptionStore);

  const showLoadMore = ref(false);
  const load = async () => {
    showLoadMore.value = true;
    await inscriptionStore.loadMore(route.query);
    showLoadMore.value = false;
  };

  const fetchData = async () => {
    dataLoading.value = true;
    await fetchCount(route.query);
    await inscriptionStore.fetchInscriptions(route.query);
    dataLoading.value = false;
  };

  const route = useRoute();
  watch(
    () => route.query,
    async () => {
      await initFilters();
    }
  );
  const initFilters = async () => {
    inscriptionFilterStore.initFiltersFromQuery(route.query);
  };

  onMounted(async () => {
    loading.value = true;
    await initFilters();
    await fetchMetadata();
    loading.value = false;
  });

  const router = useRouter();

  watch(filters, async (newValue, oldValue) => {
    let query = getQuery(filters.value);

    if (activeClientIds.value && activeClientIds.value != '') {
      query = {
        ...query,
        clientIds: activeClientIds.value
      };
    }
    if (filters_utils.methods.isDeepEqual(query, route.query)) {
      await fetchData();
    } else {
      await router.push({ query });
    }
  });

  const getQuery = (filters: Filters) =>
    api_utils.methods.formatParams(filters, [
      INSCRIPTIONS_FILTERS_CONFIGURATION.date.name,
      INSCRIPTIONS_FILTERS_CONFIGURATION.datePeriodeDebut.name,
      INSCRIPTIONS_FILTERS_CONFIGURATION.datePeriodeFin.name
    ]);

  const inscriptionCount = ref<InscriptionCount>({
    nbConventionsASigner: 0,
    nbTestPositionnementNonRealise: 0,
    nbPifsAsigner: 0
  });

  const fetchCount = (query: Filters) => {
    fetchCountPif(query);
    fetchCountConvention(query);
    fetchCountPositionnement(query);
  };

  const fetchCountPif = async (query: Filters) => {
    const params = { ...query };
    const response = await API.get(
      'client/v2/inscriptions/count/PIF_A_SIGNER',
      {
        params
      }
    );

    if (response && response.data) {
      inscriptionCount.value.nbPifsAsigner = response.data;
    }
  };

  const fetchCountConvention = async (query: Filters) => {
    const params = { ...query };
    const response = await API.get(
      'client/v2/inscriptions/count/CONVENTIONS_A_SIGNER',
      {
        params
      }
    );

    if (response && response.data) {
      inscriptionCount.value.nbConventionsASigner = response.data;
    }
  };

  const fetchCountPositionnement = async (query: Filters) => {
    const params = { ...query };
    const response = await API.get(
      'client/v2/inscriptions/count/T_POS_NON_REALISE',
      {
        params
      }
    );

    if (response && response.data) {
      inscriptionCount.value.nbTestPositionnementNonRealise = response.data;
    }
  };

  watch(activeClientIds, async () => {
    fetchMetadata();
  });

  const metadataLoaded = ref<boolean>(false);
  const metadatas = ref<InscriptionMetadata>({
    formations: [],
    participants: [],
    organisateurs: []
  });
  const fetchMetadata = async () => {
    metadataLoaded.value = false;
    void fetchMetadataFormations();
    void fetchMetadataParticipants();
    void fetchMetadataOrganisateurs();
    metadataLoaded.value = true;
  };

  const fetchMetadataFormations = async () => {
    const response = await API.get(
      `client/v2/inscriptions/metadata/formations${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.formations = response.data;
    }
  };

  const fetchMetadataOrganisateurs = async () => {
    const response = await API.get(
      `client/v2/inscriptions/metadata/organisateurs${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.organisateurs = response.data;
    }
  };

  const fetchMetadataParticipants = async () => {
    const response = await API.get(
      `client/v2/inscriptions/metadata/participants${activeClientIds.value.length ? '?clientIds=' + activeClientIds.value : ''}`
    );

    if (response && response.data) {
      metadatas.value.participants = response.data;
    }
  };
</script>

<style lang="scss" scoped></style>
